import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ShieldCheck, Save, RotateCcw, Trash2, Users, KeyRound } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CAPABILITIES, RAENGE_ORDER } from "@/lib/permissions";
import { AUTH_DISABLED } from "@/lib/constants";

export default function Administration() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [delTarget, setDelTarget] = useState(null);
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [rankConfig, setRankConfig] = useState(null);

  const loadRank = () => api.get("/rank-permissions").then((r) => setRankConfig(r.data)).catch(() => {});
  const loadAccounts = () => api.get("/auth/accounts").then((r) => setAccounts(r.data)).catch(() => {});

  useEffect(() => {
    loadRank();
    loadAccounts();
  }, []);

  const deleteAccount = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/auth/accounts/${delTarget.id}`);
      toast.success("Account gelöscht");
      loadAccounts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Löschen fehlgeschlagen");
    } finally {
      setDelTarget(null);
    }
  };

  const resetPassword = async () => {
    if (!pwTarget) return;
    if ((newPw || "").length < 6) { toast.error("Passwort: mind. 6 Zeichen"); return; }
    setPwBusy(true);
    try {
      await api.post(`/auth/accounts/${pwTarget.id}/reset-password`, { new_password: newPw });
      toast.success(`Passwort für ${pwTarget.name} zurückgesetzt`);
      setPwTarget(null);
      setNewPw("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Zurücksetzen fehlgeschlagen");
    } finally {
      setPwBusy(false);
    }
  };

  const toggle = (tier, cap) =>
    setConfig((c) => ({
      ...c,
      tiers: { ...c.tiers, [tier]: { ...c.tiers[tier], [cap]: !c.tiers[tier]?.[cap] } },
    }));

  const toggleRank = (rang, cap) =>
    setRankConfig((c) => ({ ...c, [rang]: { ...c[rang], [cap]: !c[rang]?.[cap] } }));

  const saveRank = async () => {
    try {
      await api.put("/rank-permissions", rankConfig);
      toast.success("Rang-Rechte gespeichert");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    }
  };

  const resetRank = async () => {
    try {
      const { data } = await api.delete("/rank-permissions");
      setRankConfig(data);
      toast.success("Rang-Rechte auf Standard zurückgesetzt (bitte speichern)");
    } catch (e) {
      /* ignore */
    }
  };

  const save = async () => {
    try {
      await api.put("/rank-permissions", rankConfig);
      toast.success("Gespeichert");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    }
  };

  const reset = async () => {
    try {
      const { data } = await api.delete("/rank-permissions");
      setRankConfig(data);
    } catch (e) {
      /* ignore */
    }
  };

  if (!rankConfig) {
    return (
      <div>
        <PageHeader eyebrow="SADCR · System" title="Administration" icon={ShieldCheck} />
        <div className="p-8 text-zinc-500 font-mono text-sm">Wird geladen…</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="SADCR · System"
        title="Administration"
        description="Rechte-Matrix und Rang-Zuordnung verwalten."
        icon={ShieldCheck}
      />
      <div className="p-8 space-y-6">
        {AUTH_DISABLED && (
          <div className="border border-amber-500/30 bg-amber-500/10 rounded-sm px-4 py-3 text-sm text-amber-300">
            Hinweis: Der Login ist aktuell deaktiviert – die Rechte werden gespeichert, aber noch nicht erzwungen.
            Sie greifen automatisch, sobald der Login aktiviert wird.
          </div>
        )}

        <Tabs defaultValue="rang-rechte" className="space-y-6">
          <TabsList className="bg-[#0C0E12] border border-zinc-800 rounded-sm p-1">
            <TabsTrigger
              data-testid="tab-rang-rechte"
              value="rang-rechte"
              className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-white text-zinc-400 text-sm px-4"
            >
              Rang-Rechte
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-accounts"
              value="accounts"
              className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-white text-zinc-400 text-sm px-4"
            >
              Accounts
            </TabsTrigger>
          </TabsList>

          {/* Rang-Rechte Voll-Matrix */}
          <TabsContent value="rang-rechte" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Rechte je Rang (Voll-Matrix)</h2>
              <div className="flex gap-2">
                <Button data-testid="rankperm-reset" onClick={resetRank} variant="outline" className="rounded-sm border-zinc-700 gap-2 h-8">
                  <RotateCcw className="h-4 w-4" /> Standard
                </Button>
                <Button data-testid="rankperm-save" onClick={saveRank} className="rounded-sm bg-primary hover:bg-blue-600 text-white gap-2 h-8">
                  <Save className="h-4 w-4" /> Speichern
                </Button>
              </div>
            </div>
            {!rankConfig ? (
              <div className="text-zinc-500 font-mono text-sm">Wird geladen…</div>
            ) : (
              <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500 h-11 sticky left-0 bg-[#0C0E12] z-10">Fähigkeit</TableHead>
                      {RAENGE_ORDER.map((r) => (
                        <TableHead key={r} className="text-center text-[9px] font-bold uppercase tracking-[0.04em] text-zinc-500 whitespace-nowrap px-2">{r}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {CAPABILITIES.map((cap) => (
                      <TableRow key={cap.key} className="border-zinc-800 hover:bg-zinc-900/50">
                        <TableCell className="text-sm text-zinc-200 whitespace-nowrap sticky left-0 bg-[#0C0E12]">{cap.label}</TableCell>
                        {RAENGE_ORDER.map((r) => (
                          <TableCell key={r} className="text-center">
                            <Checkbox
                              data-testid={`rankperm-${r}-${cap.key}`}
                              checked={!!rankConfig[r]?.[cap.key]}
                              onCheckedChange={() => toggleRank(r, cap.key)}
                              className="border-zinc-600 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* Accounts */}
          <TabsContent value="accounts" className="space-y-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Login-Accounts</h2>
              <span className="ml-auto font-mono text-xs text-zinc-600">{accounts.length} Account(s)</span>
            </div>
            <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500 h-11">Name</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500">Dienstnummer</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500">Rang</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500">Status</TableHead>
                    <TableHead className="text-right text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500 w-24">Aktion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.length === 0 ? (
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableCell colSpan={5} className="text-center py-10 text-zinc-600 font-mono text-sm">Keine Accounts</TableCell>
                    </TableRow>
                  ) : (
                    accounts.map((a) => {
                      const locked = a.name === "Administration";
                      return (
                        <TableRow key={a.id} data-testid="account-row" className="border-zinc-800 hover:bg-zinc-900/50">
                          <TableCell className="text-sm text-zinc-200">{a.name}</TableCell>
                          <TableCell className="font-mono text-sm text-zinc-300">{a.dienstnummer || "—"}</TableCell>
                          <TableCell className="text-sm text-zinc-300">{a.rang || "—"}</TableCell>
                          <TableCell>
                            {a.gekuendigt ? (
                              <Badge data-testid={`account-badge-gekuendigt-${a.id}`} className="rounded-sm border border-red-500/40 bg-red-500/10 text-red-400 font-mono text-[10px] uppercase tracking-wide">Gekündigt</Badge>
                            ) : a.suspendiert ? (
                              <Badge data-testid={`account-badge-suspendiert-${a.id}`} className="rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-400 font-mono text-[10px] uppercase tracking-wide">Suspendiert</Badge>
                            ) : (
                              <span className="text-zinc-600 font-mono text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                data-testid="account-reset-pw-btn"
                                size="icon"
                                variant="ghost"
                                onClick={() => { setPwTarget(a); setNewPw(""); }}
                                title="Passwort zurücksetzen"
                                className="h-8 w-8 rounded-sm text-zinc-400 hover:text-primary hover:bg-primary/10"
                              >
                                <KeyRound className="h-4 w-4" />
                              </Button>
                              <Button
                                data-testid="account-delete-btn"
                                size="icon"
                                variant="ghost"
                                disabled={locked}
                                onClick={() => setDelTarget(a)}
                                title={locked ? "Geschützter Account" : "Account löschen"}
                                className="h-8 w-8 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-30 disabled:hover:text-zinc-400 disabled:hover:bg-transparent"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!pwTarget} onOpenChange={(o) => { if (!o) { setPwTarget(null); setNewPw(""); } }}>
        <DialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-zinc-100 flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-primary" /> Passwort zurücksetzen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-zinc-400">
              Neues Passwort für <span className="text-zinc-200 font-medium">{pwTarget?.name}</span> festlegen. Das alte Passwort wird nicht benötigt.
            </p>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">Neues Passwort</Label>
              <Input
                data-testid="account-reset-pw-input"
                type="text"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="mind. 6 Zeichen"
                className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => { setPwTarget(null); setNewPw(""); }} className="rounded-sm text-zinc-400">Abbrechen</Button>
            <Button data-testid="account-reset-pw-confirm" onClick={resetPassword} disabled={pwBusy} className="rounded-sm bg-primary hover:bg-blue-600 text-white">
              {pwBusy ? "Zurücksetzen…" : "Zurücksetzen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-zinc-100">Account löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Der Login-Account von <span className="text-zinc-200 font-medium">{delTarget?.name}</span> wird dauerhaft gelöscht.
              Der zugehörige Officer-Eintrag bleibt erhalten (separat auf der Officer-Seite löschbar).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm bg-transparent border-zinc-700 text-zinc-300">Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="account-delete-confirm" onClick={deleteAccount} className="rounded-sm bg-red-600 hover:bg-red-700 text-white">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
